import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import { Turnstile } from "@marsidev/react-turnstile";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { useSecurityMonitoring } from "@/lib/security";

interface TurnstileContextType {
  // Turnstile token management
  token: string | null;
  isLoading: boolean;
  error: string | null;

  // Widget state
  isVisible: boolean;
  isReady: boolean;

  // Widget methods
  getTurnstileToken: () => Promise<string | null>;
  resetTurnstile: () => void;
  renderTurnstile: () => void;

  // Component for invisible mode
  TurnstileComponent: React.ComponentType<{
    onSuccess?: (token: string) => void;
  }>;
}

const TurnstileContext = createContext<TurnstileContextType | null>(null);

interface TurnstileProviderProps {
  children: React.ReactNode;
  siteKey?: string;
}

export const TurnstileProvider: React.FC<TurnstileProviderProps> = ({
  children,
  siteKey = import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY,
}) => {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const securityMonitor = useSecurityMonitoring();

  const getTurnstileToken = useCallback(async (): Promise<string | null> => {
    if (!siteKey) {
      const errorMsg = "Turnstile site key not configured";
      setError(errorMsg);
      securityMonitor.trackSecurityEvent(
        "turnstile_configuration_error",
        {
          error: errorMsg,
          has_site_key: false,
        },
        "high",
      );
      return null;
    }

    if (token) {
      // Return existing valid token
      securityMonitor.trackSecurityEvent(
        "turnstile_token_reused",
        {
          has_existing_token: true,
        },
        "low",
      );
      return token;
    }

    try {
      setIsLoading(true);
      setError(null);

      securityMonitor.trackSecurityEvent(
        "turnstile_token_request_started",
        {
          is_visible: isVisible,
          is_ready: isReady,
        },
        "low",
      );

      // Get token from current widget instance
      const currentToken = turnstileRef.current?.getResponse();

      if (currentToken) {
        setToken(currentToken);
        securityMonitor.trackSecurityEvent(
          "turnstile_token_obtained",
          {
            token_length: currentToken.length,
            method: "existing_widget",
          },
          "low",
        );
        return currentToken;
      }

      // If no token available, render widget in invisible mode
      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          setError("Turnstile challenge timeout");
          securityMonitor.trackSecurityEvent(
            "turnstile_timeout",
            {
              timeout_duration: 30000,
            },
            "medium",
          );
          reject(new Error("Turnstile challenge timeout"));
        }, 30000); // 30 second timeout

        // Set up one-time success callback
        const handleSuccess = (challengeToken: string) => {
          clearTimeout(timeout);
          setToken(challengeToken);
          securityMonitor.trackSecurityEvent(
            "turnstile_token_obtained",
            {
              token_length: challengeToken.length,
              method: "invisible_challenge",
            },
            "low",
          );
          resolve(challengeToken);
        };

        // Render invisible widget
        setIsVisible(true);

        // Store callback for component to use
        (
          window as unknown as {
            __turnstileSuccessCallback?: (t: string) => void;
          }
        ).__turnstileSuccessCallback = handleSuccess;
      });
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to get Turnstile token";
      setError(errorMessage);
      securityMonitor.trackSecurityEvent(
        "turnstile_error",
        {
          error_message: errorMessage,
          error_type: err instanceof Error ? err.name : "unknown",
        },
        "medium",
      );
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [token, siteKey, isVisible, isReady, securityMonitor]);

  const resetTurnstile = useCallback(() => {
    setToken(null);
    setError(null);
    setIsVisible(false);
    turnstileRef.current?.reset();

    securityMonitor.trackSecurityEvent(
      "turnstile_reset",
      {
        had_token: !!token,
        had_error: !!error,
      },
      "low",
    );
  }, [token, error, securityMonitor]);

  const renderTurnstile = useCallback(() => {
    setIsVisible(true);
    turnstileRef.current?.render();

    securityMonitor.trackSecurityEvent(
      "turnstile_render_requested",
      {
        was_visible: isVisible,
      },
      "low",
    );
  }, [isVisible, securityMonitor]);

  const handleTurnstileSuccess = useCallback(
    (challengeToken: string) => {
      setToken(challengeToken);
      setError(null);
      setIsVisible(false); // Hide after success in invisible mode

      securityMonitor.trackSecurityEvent(
        "turnstile_challenge_completed",
        {
          token_length: challengeToken.length,
          was_visible: isVisible,
        },
        "low",
      );

      // Call global callback if set (for promises)
      const win = window as unknown as {
        __turnstileSuccessCallback?: (t: string) => void;
      };
      if (win.__turnstileSuccessCallback) {
        win.__turnstileSuccessCallback(challengeToken);
        delete win.__turnstileSuccessCallback;
      }
    },
    [isVisible, securityMonitor],
  );

  const handleTurnstileError = useCallback(
    (errorCode?: string) => {
      const errorMessage = `Turnstile challenge failed${errorCode ? `: ${errorCode}` : ""}`;
      setError(errorMessage);
      setIsLoading(false);

      securityMonitor.trackSecurityEvent(
        "turnstile_challenge_failed",
        {
          error_code: errorCode || "unknown",
          was_visible: isVisible,
        },
        "medium",
      );
    },
    [isVisible, securityMonitor],
  );

  const handleTurnstileExpired = useCallback(() => {
    setToken(null);
    setError("Turnstile token expired");

    securityMonitor.trackSecurityEvent(
      "turnstile_token_expired",
      {
        had_token: !!token,
      },
      "low",
    );
  }, [token, securityMonitor]);

  // Invisible Turnstile Component
  const TurnstileComponent: React.ComponentType<{
    onSuccess?: (token: string) => void;
  }> = useCallback(
    ({ onSuccess }) => {
      if (!siteKey || !isVisible) return null;

      return (
        <div
          style={{
            position: "absolute",
            left: "-9999px",
            opacity: 0,
            pointerEvents: "none",
          }}
        >
          <Turnstile
            ref={turnstileRef}
            siteKey={siteKey}
            options={{
              appearance: "interaction-only", // Invisible mode
              theme: "light",
              size: "invisible",
              action: "telegram-auth",
            }}
            onSuccess={(token) => {
              handleTurnstileSuccess(token);
              onSuccess?.(token);
            }}
            onError={handleTurnstileError}
            onExpire={handleTurnstileExpired}
            onLoad={() => {
              setIsReady(true);
              securityMonitor.trackSecurityEvent(
                "turnstile_widget_loaded",
                {
                  site_key_configured: !!siteKey,
                },
                "low",
              );
            }}
          />
        </div>
      );
    },
    [
      siteKey,
      isVisible,
      handleTurnstileSuccess,
      handleTurnstileError,
      handleTurnstileExpired,
      securityMonitor,
    ],
  );

  const contextValue: TurnstileContextType = {
    token,
    isLoading,
    error,
    isVisible,
    isReady,
    getTurnstileToken,
    resetTurnstile,
    renderTurnstile,
    TurnstileComponent,
  };

  return (
    <TurnstileContext.Provider value={contextValue}>
      {children}
      <TurnstileComponent />
    </TurnstileContext.Provider>
  );
};

export const useTurnstile = (): TurnstileContextType => {
  const context = useContext(TurnstileContext);
  if (!context) {
    throw new Error("useTurnstile must be used within a TurnstileProvider");
  }
  return context;
};

// Helper hook for authentication flow
export const useTurnstileAuth = () => {
  const turnstile = useTurnstile();
  const securityMonitor = useSecurityMonitoring();

  const getTurnstileTokenForAuth = useCallback(async (): Promise<
    string | null
  > => {
    try {
      securityMonitor.trackSecurityEvent(
        "auth_turnstile_request",
        {
          has_existing_token: !!turnstile.token,
        },
        "low",
      );

      const token = await turnstile.getTurnstileToken();

      if (token) {
        securityMonitor.trackSecurityEvent(
          "auth_turnstile_success",
          {
            token_length: token.length,
          },
          "low",
        );
      } else {
        securityMonitor.trackSecurityEvent(
          "auth_turnstile_failed",
          {
            error: turnstile.error,
          },
          "medium",
        );
      }

      return token;
    } catch (error: unknown) {
      securityMonitor.trackSecurityEvent(
        "auth_turnstile_error",
        {
          error_message:
            error instanceof Error ? error.message : "Unknown error",
        },
        "high",
      );
      return null;
    }
  }, [turnstile, securityMonitor]);

  return {
    ...turnstile,
    getTurnstileTokenForAuth,
  };
};

export default TurnstileContext;
